import {
  Box,
  Card,
  CardBody,
  CardHeader,
  HStack,
  LinkBox,
  LinkOverlay,
  Skeleton,
  Text,
  VStack
} from "@chakra-ui/react"
import { BlocksTemplateVariablesType } from "@pathwright/blocks-engine/src/types"
import { IconName } from "@pathwright/pathicons"
import { contentStyles } from "@pathwright/ui/src/components/content/typography"
import Screensize, {
  getScreensizeForWidth
} from "@pathwright/ui/src/components/ui/Screensize"
import ResourceBackground from "@pathwright/ux/src/components/background/ResourceBackground"
import Link from "@pathwright/ux/src/components/link/Link"
import Pathicon from "@pathwright/ux/src/components/pathicon/Pathicon"
import { useEffect, useState } from "react"
import BlockContent from "../../blocks/BlockContent"
import { getStepVerbIcon } from "../../path/utils"
import { usePathwrightContext } from "../../pathwright/PathwrightContext"
import { useCoverPageContext } from "../CoverPageProvider"
import { useCoverPageTranslate } from "../utils"
// import { PreviewPathDotNav } from "./PathPreviewNavigator"
import { useSuppressNavigationClickAfterDrag } from "@pathwright/utils/src/mouse/useSuppressNavigationClickAfterDrag"
import { PreviewableStepProps } from "./types"
import usePreviewContentRef from "./usePreviewContentRef"

const useStepContainerWidth = (ref: React.RefObject<HTMLElement>) => {
  const [width, setWidth] = useState<number | undefined>()
  useEffect(() => {
    const el = ref.current
    if (!el || typeof ResizeObserver === "undefined") return
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect?.width
      if (typeof w === "number") setWidth(w)
    })
    if (typeof el.clientWidth === "number") setWidth(el.clientWidth)
    ro.observe(el)
    return () => ro.disconnect()
  }, [ref])
  return width
}

const mockUser = {
  id: 1,
  first_name: "John",
  last_name: "Doe",
  display_name: "John Doe",
  email: "john.doe@example.com"
}

export const PreviewStepCard = (props: PreviewableStepProps) => {
  const { t } = useCoverPageTranslate()
  const { data: step, isImmersive, maxItemHeight } = props
  const { school } = usePathwrightContext()
  const resource = useCoverPageContext((v) => v.resource)
  const me = useCoverPageContext((v) => v.me)
  const immersiveActive = useCoverPageContext((v) => v.immersiveActive)
  const getImmersiveRoutePath = useCoverPageContext(
    (v) => v.getImmersiveRoutePath
  )

  const [isLoading, setIsLoading] = useState(true)
  const stepContainerRef = usePreviewContentRef(!isImmersive)
  const stepContainerWidth = useStepContainerWidth(stepContainerRef)

  const blocksTemplateVariables = {
    step,
    school,
    user: me || mockUser
  } as BlocksTemplateVariablesType

  const pathRoute = getImmersiveRoutePath?.({ name: "preview", id: "path" })
  const cardPreviewRelativeTo =
    !isImmersive && !immersiveActive && step?.source_id != null
      ? `preview/${Number(step.source_id)}`
      : null

  const { onPointerDownCapture, onClickCapture } =
    useSuppressNavigationClickAfterDrag()

  return (
    <LinkBox
      className={isImmersive ? "CoverPagePreviewStep" : ""}
      onPointerDownCapture={onPointerDownCapture}
      onClickCapture={onClickCapture}
      shadow={isImmersive ? "none" : "card"}
      borderRadius={"card"}
      height={isImmersive ? "100%" : undefined}
      maxHeight={isImmersive ? "100%" : undefined}
      w={isImmersive ? "100%" : undefined}
      display={isImmersive ? "flex" : undefined}
      flexDirection={isImmersive ? "column" : undefined}
      p={isImmersive || immersiveActive ? 0 : 4}
      bgColor="#F5F5F5"
      css={{
        "& .ResourceBackground": {
          position: "absolute"
        }
      }}
      h={isImmersive ? "100vh" : "100%"}
      pointerEvents="auto"
      cursor={
        isImmersive || immersiveActive
          ? "default"
          : cardPreviewRelativeTo
          ? "pointer"
          : "default"
      }
    >
      {cardPreviewRelativeTo ? (
        <Link
          as={LinkOverlay}
          relativeTo={cardPreviewRelativeTo}
          aria-label={t("path_preview.open_preview", {
            name: step?.name ?? t("path_preview.step")
          })}
          css={{ position: "absolute", inset: 0, zIndex: 1 }}
          borderRadius="card"
          _focusVisible={{
            outline: "2px solid",
            outlineColor: "brand.500",
            outlineOffset: "-2px"
          }}
        >
          <Box as="span" display="block" h="100%" w="100%" />
        </Link>
      ) : null}
      {!isImmersive && !immersiveActive && (
        <Box pointerEvents={cardPreviewRelativeTo ? "none" : "auto"}>
          <ResourceBackground resource={resource} forceBlur />
        </Box>
      )}
      <Card.Root
        bgColor="white"
        borderRadius="card"
        w="100%"
        h={isImmersive ? undefined : "100%"}
        flex={isImmersive ? 1 : undefined}
        pointerEvents={
          cardPreviewRelativeTo ? "none" : isImmersive ? "auto" : "none"
        }
        {...(isImmersive && {
          maxWidth: "1000px",
          margin: "0 auto",
          overflowY: "auto"
        })}
        overflow={isImmersive ? "auto" : "hidden"}
      >
        <CardHeader pb={2} pt={4}>
          <HStack align="center" gap={3} justify="space-between">
            <HStack align="center" gap={3}>
              {step?.step_verb ? (
                <Pathicon
                  icon={getStepVerbIcon(step.step_verb) as IconName}
                  color="#aaa"
                  size="1em"
                />
              ) : null}
              <VStack align="start" gap="1px" overflow="hidden">
                <Text
                  as="h4"
                  fontWeight="700"
                  fontSize="md"
                  color={"inherit"}
                  lineClamp={2}
                  textOverflow="ellipsis"
                  whiteSpace="nowrap"
                  overflow="hidden"
                  maxW="100%"
                >
                  {step?.name}
                </Text>
                <HStack
                  gap="5px"
                  separator={
                    <Text
                      as="span"
                      m={0}
                      border="none"
                      textStyle="xs"
                      color="#888"
                    >
                      •
                    </Text>
                  }
                >
                  {!step.is_required ? (
                    <Text
                      m={0}
                      fontWeight="400"
                      as="small"
                      textStyle="xs"
                      color="#888"
                    >
                      {t("path_preview.optional")}
                    </Text>
                  ) : null}
                  {!!step.time_to_complete && (
                    <Text
                      m={0}
                      fontWeight="400"
                      as="small"
                      textStyle="xs"
                      color="#888"
                    >
                      {step.time_to_complete}
                    </Text>
                  )}
                  {step.userpoints_value && (
                    <Text
                      m={0}
                      fontWeight="400"
                      as="small"
                      textStyle="xs"
                      color="#888"
                    >
                      {t("path_preview.points", {
                        count: step.userpoints_value
                      })}
                    </Text>
                  )}
                </HStack>
              </VStack>
            </HStack>
            {isImmersive && pathRoute ? (
              <Link
                to={pathRoute}
                variant="plain"
                paddingX={2}
                color="SECONDARY_GRAY"
                textDecoration="none"
                _hover={{
                  color: "PRIMARY_GRAY",
                  textDecoration: "none"
                }}
                pointerEvents="auto"
                position="relative"
                zIndex={1}
              >
                <Pathicon icon="list-back" size="1em" />
                {t("path_preview.back_to_path")}
              </Link>
            ) : null}
          </HStack>
        </CardHeader>
        <CardBody w="100%" p={0} pointerEvents={isImmersive ? "auto" : "none"}>
          {isLoading ? (
            <Skeleton
              h={maxItemHeight}
              w="100%"
              css={{
                "--start-color": "colors.blackAlpha.50",
                "--end-color": "colors.blackAlpha.300"
              }}
            />
          ) : null}
          <Screensize.Provider
            screensize={getScreensizeForWidth(stepContainerWidth ?? 0)}
          >
            <Box
              className="CoverPagePreviewStep__content"
              w="100%"
              ref={stepContainerRef}
              css={{ ...contentStyles({ containerWidth: stepContainerWidth }) }}
            >
              <BlockContent
                accountID={step.context?.school_id!}
                contextKey={`/${resource?.id}/${resource?.preview_group?.id}/${step.source_id}`}
                isPreviewMode
                onDataLoaded={() => {
                  setIsLoading(false)
                  requestAnimationFrame(() => {
                    window.dispatchEvent(new Event("resize"))
                  })
                }}
                templateVariables={blocksTemplateVariables}
              />
            </Box>
          </Screensize.Provider>
        </CardBody>
      </Card.Root>
    </LinkBox>
  )
}
